'use client'

import { useTranslations } from 'next-intl'
import type React from 'react'
import { createContext, useContext, useMemo, useState } from 'react'
import { useLocalStorage } from 'usehooks-ts'

import CkEditorCSRRenderer from '@/components/elementary/CkEditorRenderer/CkEditorCSRRenderer'
import { Paragraph } from '@/components/typography/Paragraph'
import { Button } from '@/components/ui/button'
import {
	Dialog,
	DialogContent,
	DialogFooter,
	DialogHeader,
	DialogTitle,
	DialogTrigger,
} from '@/components/ui/dialog'
import { cn } from '@/lib/styles'

interface Props {
	readonly Props?: React.ReactNode
	readonly header?: {
		title?: string
		description?: string
	}
	readonly children: React.ReactNode
	readonly hideCloseIcon?: boolean
	readonly dialogContentClassName?: string
	readonly confirmDialogClose?: boolean
	readonly dialogCloseCallback?: () => void
	readonly closeButtonText?: string
	readonly showOnceKey?: string
	readonly showOnceDelay?: number
	readonly contentTestId?: string
}

const DialogContext = createContext({
	closeModal: () => {},
	setConfirmDialogClose: (_: boolean) => {},
	confirmClose: false,
})

export const useDialogContext = () => useContext(DialogContext)

export function AppDialog({
	Props: triggerButton,
	header,
	children,
	dialogContentClassName,
	confirmDialogClose,
	dialogCloseCallback,
	hideCloseIcon,
	closeButtonText,
	showOnceKey,
	showOnceDelay = 0,
	contentTestId,
}: Props) {
	//TODO: refactor - Move showOnceKey and showOnceDelay logic outside AppDialog component
	const t = useTranslations('comps.dialog')

	const [storedHasShown, setStoredHasShown] = useLocalStorage<boolean>(
		showOnceKey || 'defaultKey',
		false,
	)
	const hasShown = showOnceKey ? storedHasShown : false

	const [open, setOpen] = useState<boolean>(() => {
		if (triggerButton) return false
		if (showOnceKey && showOnceKey !== 'defaultKey' && hasShown) return false
		if (showOnceDelay > 0) {
			setTimeout(() => {
				setOpen(true)
			}, showOnceDelay)
			return false
		}
		return true
	})

	const [confirmClose, setConfirmClose] = useState<boolean>(confirmDialogClose ?? false)

	const providerValue = useMemo(
		() => ({
			closeModal: () => setOpen(false),
			confirmClose,
			setConfirmDialogClose: (value: boolean) => setConfirmClose(value),
		}),
		[confirmClose],
	)

	const handleOpenChange = (open: boolean) => {
		if (showOnceKey) setStoredHasShown(true)
		if (!!confirmClose && !open && !confirm(t('confirmClose'))) return

		if (!open) {
			if (dialogCloseCallback) dialogCloseCallback()
		}
		setConfirmClose(confirmDialogClose ?? false)
		setOpen(open)
	}

	return (
		<Dialog open={open} onOpenChange={handleOpenChange}>
			{triggerButton && <DialogTrigger asChild>{triggerButton}</DialogTrigger>}
			<DialogContent
				hideCloseIcon={hideCloseIcon}
				onInteractOutside={() => {
					if (showOnceKey) setStoredHasShown(true)
					setOpen(false)
				}}
				className={cn('max-h-screen overflow-auto', dialogContentClassName)}
				data-test-id={contentTestId}
			>
				<DialogHeader className="mb-4">
					<DialogTitle>
						<CkEditorCSRRenderer htmlContent={header?.title} />
					</DialogTitle>

					<CkEditorCSRRenderer htmlContent={header?.description} />
				</DialogHeader>
				<DialogContext.Provider value={providerValue}>{children}</DialogContext.Provider>
				{closeButtonText && (
					<DialogFooter className="mx-auto">
						<Button
							size="none"
							variant="primaryLink"
							onClick={() => {
								if (showOnceKey) setStoredHasShown(true)
								setOpen(false)
							}}
							className="hover:underline border-none hover:text-brand-black focus-visible:text-brand-black outline-none active:text-brand-black"
						>
							<Paragraph fontSize="bodyBoldMedium" className="text-center text-brand-black">
								{closeButtonText}
							</Paragraph>
						</Button>
					</DialogFooter>
				)}
			</DialogContent>
		</Dialog>
	)
}
